/** Shared post display helpers — preview, feed, and details */
import type { TranslateFn, TaggedBuddy, TaggedBuddiesParts } from '../types';

const HASHTAG_REGEX = /(#[a-zA-Z0-9_]+)/g;

/** Extracts the first name from a full display name (e.g. "Sarah Miller" → "Sarah"). */
const getFirstName = (fullName: string | null | undefined): string => {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
};

const buildFeelingPrefix = (
  feelingKey: string | null | undefined,
  t: TranslateFn,
): string => {
  if (!feelingKey) {
    return '';
  }
  const feelingLabel = t(`home.createPost.feeling.${feelingKey}`).toLowerCase();
  return t('home.createPost.feelingPrefix', { feeling: feelingLabel });
};

/**
 * Canonical form of a hashtag: the bare label, no `#` and no inner whitespace.
 * The composer state, the `HashTags` request field and the appended body text
 * all build on this, so a value that already carries a `#` never gains a second
 * one when it is read back and saved again.
 */
const normalizeHashtagLabel = (value: string): string =>
  // Whitespace goes first: a leading space would otherwise hide the `#` from the
  // prefix strip and leave it on the label.
  String(value ?? '').replace(/\s+/g, '').replace(/^#+/, '');

/**
 * Canonical labels for a hashtag list. Takes whatever a caller or the API
 * supplies — bare, `#`-prefixed, `##`-prefixed, or several tags packed into one
 * comma-separated entry — and drops blanks and repeats.
 */
const normalizeHashtagList = (
  hashtags?: readonly string[] | null,
): string[] => {
  const labels: string[] = [];
  (hashtags ?? []).forEach(entry => {
    String(entry ?? '')
      .split(/[,\s]+/)
      .forEach(part => {
        const label = normalizeHashtagLabel(part);
        if (label && !labels.includes(label)) {
          labels.push(label);
        }
      });
  });
  return labels;
};

/**
 * Collapses a repeated `#` at the start of a tag token back to one, repairing
 * bodies saved by the build that re-appended an already-prefixed tag. Only a run
 * that opens a token and runs into a word character is touched, so `#` used as
 * punctuation mid-sentence is left alone.
 */
const collapseHashtagPrefixes = (text: string): string =>
  text.replace(/(^|\s)#{2,}(?=[\p{L}\p{N}_])/gu, '$1#');

const appendHashtagsToText = (
  text: string,
  hashtags: readonly string[] = [],
): string => {
  let result = text.trim();
  normalizeHashtagList(hashtags).forEach(label => {
    const tagText = `#${label}`;
    if (!result.includes(tagText)) {
      result = result ? `${result} ${tagText}` : tagText;
    }
  });
  return result;
};

interface BuildFullPostTextInput {
  content?: string;
  feelingKey?: string | null;
  hashtags?: string[];
  t: TranslateFn;
}

const buildFullPostText = ({
  content = '',
  feelingKey,
  hashtags = [],
  t,
}: BuildFullPostTextInput): string => {
  const body = content.trim();
  const prefix = buildFeelingPrefix(feelingKey, t);

  let text = body;
  if (prefix) {
    text = body ? `${prefix} ${body}` : prefix;
  }

  return appendHashtagsToText(text, hashtags);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readTaggedField = (
  record: Record<string, unknown>,
  key: string,
): string | number | null | undefined => {
  const value = record[key];
  if (value == null) return value;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return undefined;
};

const mapTaggedUsersToBuddies = (taggedUsers?: unknown[] | null): TaggedBuddy[] => {
  if (!Array.isArray(taggedUsers) || !taggedUsers.length) {
    return [];
  }
  return taggedUsers
    .map((u: unknown, idx: number): TaggedBuddy => {
      const fallbackId = `tag-${idx}`;

      if (typeof u === 'string') {
        return { id: `${fallbackId}-${u}`, name: u, profileUserGuid: null };
      }
      if (typeof u === 'number') {
        return { id: String(u), name: String(u), profileUserGuid: null };
      }
      if (isRecord(u)) {
        // GetFeeds: `{ profileUserGuid, username }` only. Legacy/create payloads
        // may still include id/userId/avatar.
        const rawGuid =
          readTaggedField(u, 'profileUserGuid') ??
          readTaggedField(u, 'ProfileUserGuid') ??
          readTaggedField(u, 'userGuid') ??
          readTaggedField(u, 'UserGuid');
        const profileUserGuid =
          rawGuid != null && String(rawGuid).trim() !== ''
            ? String(rawGuid).trim()
            : null;
        const rawId = readTaggedField(u, 'id') ?? readTaggedField(u, 'userId');
        const rawName =
          readTaggedField(u, 'firstName') ??
          readTaggedField(u, 'FirstName') ??
          readTaggedField(u, 'username') ??
          readTaggedField(u, 'Username') ??
          readTaggedField(u, 'userName') ??
          readTaggedField(u, 'UserName') ??
          readTaggedField(u, 'name') ??
          readTaggedField(u, 'Name') ??
          '';
        const avatar = u.userPhoto ?? u.avatar ?? null;
        const numericOrLegacyId =
          rawId != null && String(rawId).trim() !== ''
            ? String(rawId)
            : undefined;

        return {
          // Prefer real userId when present (tag picker / write path); otherwise
          // use profileUserGuid as a stable list key — never required for navigation.
          id: numericOrLegacyId ?? profileUserGuid ?? fallbackId,
          name: String(rawName),
          avatar: avatar as TaggedBuddy['avatar'],
          profileUserGuid,
        };
      }
      return { id: fallbackId, name: '', profileUserGuid: null };
    })
    .filter(buddy => Boolean(buddy.name && buddy.name.trim() !== ''));
};

const toTaggedNameSegment = (buddy: TaggedBuddy) => ({
  type: 'bold' as const,
  value: buddy.name || '',
  profileUserGuid: buddy.profileUserGuid ?? null,
});

/**
 * Stable identity for a tagged buddy (toggle / remove / list keys).
 * Prefer GUID, then id, then name — never collapse missing fields to a shared
 * `undefined` that would make two buddies collide under `===`.
 */
const buddyKey = (
  buddy: Pick<TaggedBuddy, 'profileUserGuid' | 'id' | 'name'>,
): string => {
  const raw = buddy.profileUserGuid ?? buddy.id ?? buddy.name ?? '';
  return typeof raw === 'string' ? raw : String(raw);
};

const getTaggedBuddiesParts = (
  authorName: string,
  taggedBuddies: TaggedBuddy[] = [],
  t: TranslateFn,
): TaggedBuddiesParts => {
  if (!taggedBuddies?.length) {
    return { authorName, segments: [] };
  }

  const buddies = taggedBuddies.filter(buddy =>
    Boolean(buddy?.name && buddy.name.trim() !== ''),
  );

  if (!buddies.length) {
    return { authorName, segments: [] };
  }

  if (buddies.length === 1) {
    return {
      authorName,
      segments: [
        { type: 'text', value: ` ${t('home.createPost.tagged.isWith')} ` },
        toTaggedNameSegment(buddies[0]),
      ],
    };
  }

  if (buddies.length === 2) {
    return {
      authorName,
      segments: [
        { type: 'text', value: ` ${t('home.createPost.tagged.isWith')} ` },
        toTaggedNameSegment(buddies[0]),
        { type: 'text', value: ` ${t('home.createPost.tagged.and')} ` },
        toTaggedNameSegment(buddies[1]),
      ],
    };
  }

  const othersCount = buddies.length - 1;
  return {
    authorName,
    segments: [
      { type: 'text', value: ` ${t('home.createPost.tagged.isWith')} ` },
      toTaggedNameSegment(buddies[0]),
      {
        type: 'text',
        value: ` ${t('home.createPost.tagged.and')} ${t('home.createPost.tagged.others', { count: othersCount })}`,
      },
    ],
  };
};

export interface SeparateHashtagsResult {
  caption: string;
  separateHashtags: string[];
}

const HASHTAG_TOKEN = /^#+([\p{L}\p{N}_]+)$/u;

const splitSeparateHashtags = (
  text: string,
  hashtags?: readonly string[] | null,
): SeparateHashtagsResult => {
  const normalizedTags = normalizeHashtagList(hashtags);
  const tagSet = new Set(normalizedTags.map(t => t.toLowerCase()));
  let currentText = (text || '').trimEnd();
  const stripped: string[] = [];

  while (currentText.length > 0) {
    const matchLastSpace = currentText.search(/\s[^\s]*$/);
    const token =
      matchLastSpace >= 0 ? currentText.slice(matchLastSpace).trimStart() : currentText;
    const match = token.match(HASHTAG_TOKEN);
    if (match) {
      const bareTag = normalizeHashtagLabel(match[1]);
      if (tagSet.size === 0 || tagSet.has(bareTag.toLowerCase())) {
        if (!stripped.includes(bareTag)) {
          stripped.unshift(bareTag);
        }
        currentText =
          matchLastSpace >= 0 ? currentText.slice(0, matchLastSpace).trimEnd() : '';
        continue;
      }
    }
    break;
  }

  // Prefer the spelling from the rendered trailing tokens, then append any
  // selected tags not present in `text`. Hashtag identity is case-insensitive,
  // so API casing differences must not produce duplicate chips.
  const combinedTags = [...stripped, ...normalizedTags].reduce<string[]>(
    (result, tag) => {
      const normalized = normalizeHashtagLabel(tag);
      if (
        normalized &&
        !result.some(existing => existing.toLowerCase() === normalized.toLowerCase())
      ) {
        result.push(normalized);
      }
      return result;
    },
    [],
  );

  return {
    caption: currentText,
    separateHashtags: combinedTags,
  };
};

export {
  HASHTAG_REGEX,
  buildFeelingPrefix,
  appendHashtagsToText,
  collapseHashtagPrefixes,
  normalizeHashtagLabel,
  normalizeHashtagList,
  splitSeparateHashtags,
  buildFullPostText,
  buddyKey,
  getFirstName,
  getTaggedBuddiesParts,
  mapTaggedUsersToBuddies,
};
